class LoadMoreButton {
  constructor() {
    this.button = document.querySelector(".siccas-load-more__button");
    if (!this.button) {
      // The button may not exist upon initialization, add a global handler
      this.setupGlobalClickHandler();
      return;
    }

    this.spinner = this.button.querySelector(".siccas-load-more__spinner");
    this.buttonText = this.button.querySelector(".siccas-load-more__text");

    // Find all product grids on the page
    const allProductGrids = document.querySelectorAll(".product-grid");

    // We need to access both grids (before and after the block)
    this.productGrids = {
      first: allProductGrids.length > 0 ? allProductGrids[0] : null,
      last:
        allProductGrids.length > 1
          ? allProductGrids[allProductGrids.length - 1]
          : null,
    };

    this.currentPage = parseInt(this.button.dataset.currentPage, 10);
    this.totalPages = parseInt(this.button.dataset.totalPages, 10);
    this.loadedPages = [this.currentPage]; // Keep track of loaded pages

    // Extract base URL (without page parameter)
    this.baseUrl = this.extractBaseUrl(this.button.dataset.url);

    // Initialize
    this.init();

    // Watch for DOM changes to catch new buttons after filtering
    this.setupMutationObserver();
  }

  setupGlobalClickHandler() {
    // Add global delegated event handler that will catch all .siccas-load-more__button clicks
    document.addEventListener("click", (e) => {
      const button = e.target.closest(".siccas-load-more__button");
      if (button && !button.hasAttribute("data-has-event-listener")) {
        // Prevent the default button behavior
        e.preventDefault();

        // Mark the button to avoid duplicate processing
        button.setAttribute("data-has-event-listener", "true");

        // Process the click using our custom handler
        this.handleButtonClick(button);
      }
    });
  }

  async handleButtonClick(button) {
    // Get all needed elements and data
    const spinner = button.querySelector(".siccas-load-more__spinner");
    const buttonText = button.querySelector(".siccas-load-more__text");
  
    // Get page info from button's data attributes
    const currentPage = parseInt(button.dataset.currentPage, 10);
    const totalPages = parseInt(button.dataset.totalPages, 10);
    const nextPageUrl = button.dataset.url;
  
    // Don't proceed if we've loaded all pages
    if (currentPage >= totalPages) {
      button.disabled = true;
      buttonText.textContent = "All products loaded";
      return;
    }
  
    // Show loading spinner (add rotation class instead of display: inline-block)
    spinner.classList.add("siccas-load-more__spinner-rotate");
    button.disabled = true;
  
    try {
      // Fetch the next page
      const response = await fetch(nextPageUrl);
  
      if (!response.ok) {
        throw new Error("Network response was not ok");
      }
  
      const html = await response.text();
  
      // Parse the HTML
      const parser = new DOMParser();
      const doc = parser.parseFromString(html, "text/html");
  
      // Find ALL product grids in the response
      const responseProductGrids = doc.querySelectorAll(".product-grid");
      let newProducts = [];
  
      // Collect all products from all product grids, including ad blocks
      responseProductGrids.forEach((grid) => {
        const products = grid.querySelectorAll("li");
        products.forEach((product) => {
          newProducts.push(product);
        });
      });
  
      // Find the last product grid on the current page
      const allProductGrids = document.querySelectorAll(".product-grid");
      const lastGrid =
        allProductGrids.length > 0
          ? allProductGrids[allProductGrids.length - 1]
          : null;
  
      // Append new products to the last grid
      if (lastGrid && newProducts.length > 0) {
        newProducts.forEach((product) => {
          lastGrid.appendChild(product.cloneNode(true));
        });
  
        // Initialize functionality for new products
        this.initializeProductFunctionality(lastGrid);
      }
  
      // Update button state for next page
      const nextPage = currentPage + 1;
      button.dataset.currentPage = nextPage;
  
      // Generate URL for the next page (preserving sort and filter params)
      const baseUrl = this.extractBaseUrl(nextPageUrl);
      button.dataset.url =
        baseUrl +
        (baseUrl.includes("?") ? "&" : "?") +
        "page=" +
        (nextPage + 1);
  
      // Hide button if we're on the last page
      if (nextPage >= totalPages) {
        button.style.display = "none";
      }
    } catch (error) {
      console.error("Error loading more products:", error);
      buttonText.textContent = "Error loading products. Try again.";
    } finally {
      // Hide spinner by removing rotation class instead of display: none
      spinner.classList.remove("siccas-load-more__spinner-rotate");
      button.disabled = false;
    }
  }

  setupMutationObserver() {
    // Monitor DOM changes to detect when a new load-more button is added after filtering
    const observer = new MutationObserver((mutations) => {
      mutations.forEach((mutation) => {
        if (mutation.type === "childList" && mutation.addedNodes.length) {
          // Look for newly added load-more buttons that don't have our handler yet
          const newButtons = document.querySelectorAll(
            ".siccas-load-more__button:not([data-has-event-listener])"
          );

          newButtons.forEach((button) => {
            // Mark the button to avoid attaching multiple handlers
            button.setAttribute("data-has-event-listener", "true");

            // Attach click handler directly
            button.addEventListener("click", (e) => {
              e.preventDefault();
              this.handleButtonClick(button);
            });
          });
        }
      });
    });

    // Start observing the document with all its child nodes
    observer.observe(document.body, {
      childList: true,
      subtree: true,
    });
  }

  extractBaseUrl(url) {
    // Remove page parameter from URL but preserve other parameters (like sort_by)
    return url.replace(/([?&])page=\d+(&|$)/, "$1").replace(/[?&]$/, "");
  }

  generateNextPageUrl(page) {
    // Add page parameter to base URL
    return (
      this.baseUrl + (this.baseUrl.includes("?") ? "&" : "?") + "page=" + page
    );
  }

  init() {
    // Add event listener to the button
    this.button.setAttribute("data-has-event-listener", "true");
    this.button.addEventListener("click", (e) => {
      e.preventDefault();
      this.handleButtonClick(this.button);
    });

    // Mark current page as loaded in pagination
    this.updatePagination();
  }

  updatePagination() {
    // Find all pagination items
    const paginationItems = document.querySelectorAll(".pagination__item");

    // Mark loaded pages
    paginationItems.forEach((item) => {
      const pageNum = parseInt(item.textContent.trim(), 10);
      if (!isNaN(pageNum) && this.loadedPages.includes(pageNum)) {
        item.classList.add("pagination__item--loaded");
      }
    });
  }

  initializeProductFunctionality(container) {
    // Re-initialize any product functionality on new products
    if (!container) return;

    // Find all detail buttons in newly added products
    const detailButtons = container.querySelectorAll(
      '.siccas-recommendations__button--details:not([data-has-listener="true"])'
    );

    detailButtons.forEach((button) => {
      // Mark button as having listener
      button.setAttribute("data-has-listener", "true");

      button.addEventListener("click", function (e) {
        e.stopPropagation();
        e.preventDefault();

        const productCard = this.closest(
          ".siccas-recommendations__product-card"
        );
        if (productCard) {
          productCard.classList.toggle("details-active");
        }
      });
    });
  }
}

// Initialize on DOM content loaded
document.addEventListener("DOMContentLoaded", function () {
  new LoadMoreButton();
});

// Ensure we reinitialize after page loads with filters
window.addEventListener("load", function () {
  // Find all buttons that might have been added after initial load
  const buttons = document.querySelectorAll(
    ".siccas-load-more__button:not([data-has-event-listener])"
  );
  if (buttons.length > 0) {
    new LoadMoreButton();
  }
});
